import { Button, Icon, VStack, Box, Divider, Text } from '@chakra-ui/react';
import { UserContentLayout } from '../../components/Layouts/UserContentLayout';
import { IoAddCircleOutline } from 'react-icons/io5';
import { Fragment, useEffect, useState } from 'react';
import { Member } from '../../components/User/Member/Member';
import { MemberQrCodeModal } from '../../components/Modals/MemberQrCodeModal';
import { useRequest, useScore, useToggle } from '../../hooks';
import { useNavigate } from 'react-router-dom';
import { API_ROUTERS, ROUTERS } from '../../data';
import { useTranslation } from 'react-i18next';
import { MemberScoreModal } from '../../components/Modals/MemberScoreModal';

const MEMBER_MODALS = {
  CODE: 'CODE',
  SCORE: 'SCORE',
};

interface IMember {
  id?: string | number;
  avatar?: string;
  birthday?: string;
  code?: string;
  email?: string;
  firstName?: string;
  gender?: string;
  lastName?: string;
  mobilePhone?: string;
  postalCode?: string;
}

const UserMembers = () => {
  const { t } = useTranslation();
  const [members, setMembers] = useState<IMember[]>();
  const navigate = useNavigate();
  const { isShow, show, hide } = useToggle();
  const { authRequest } = useRequest();
  const { fetchScore } = useScore();

  useEffect(() => {
    const fetchMembers = async () => {
      const { data } = await authRequest({ method: 'get', url: API_ROUTERS.MEMBERS });
      setMembers(data);
    };

    fetchMembers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onOpenScoreModal = async (member: IMember) => {
    if (member?.id) {
      const score = await fetchScore(member?.id);
      if (score) {
        show({ type: MEMBER_MODALS.SCORE, data: { ...member, score } });
      }
    }
  };

  return (
    <>
      <UserContentLayout caption={`${t('members.page.caption')}`}>
        <Button
          mt={{ base: 6, lg: 10 }}
          variant='linkUnderline'
          color='brand.cyan'
          fontSize={{ base: 'md', lg: 'lg' }}
          leftIcon={<Icon as={IoAddCircleOutline} fontSize={{ base: 25, lg: 30 }} mb={{ base: -1, lg: -2 }} />}
          onClick={() => navigate(ROUTERS.USER.CREATE_AND_EDIT_MEMBER)}
        >
          {t('members.add.new.btn')}
        </Button>

        <VStack mt={6} maxW='2xl'>
          {members?.length ? (
            members.map((member, i) => (
              <Fragment key={`${member.birthday}-${i}`}>
                <Box w='100%' py={4}>
                  <Member
                    {...member}
                    onClickCodeIcon={() => show({ type: MEMBER_MODALS.CODE, data: member })}
                    onClickEdit={() => navigate(ROUTERS.USER.CREATE_AND_EDIT_MEMBER, { state: { member } })}
                    onClickScore={() => onOpenScoreModal(member)}
                  />
                </Box>
                <Divider />
              </Fragment>
            ))
          ) : (
            <Text w='100%'>{t('members.empty.list')}</Text>
          )}
        </VStack>
      </UserContentLayout>

      {/* ──── Modals ──────────────────────────────────────────────────────────────────────────── */}
      <MemberQrCodeModal
        isOpen={isShow?.type === MEMBER_MODALS.CODE}
        firstName={isShow?.data?.firstName}
        lastName={isShow?.data?.lastName}
        code={isShow?.data?.code}
        onClose={hide}
      />
      <MemberScoreModal
        isOpen={isShow?.type === MEMBER_MODALS.SCORE}
        onClose={hide}
        firstName={isShow?.data?.firstName}
        lastName={isShow?.data?.lastName}
        score={isShow?.data?.score}
      />
    </>
  );
};

export default UserMembers;
